import React, { useState, useEffect } from 'react';
import { FiSearch, FiTrash2, FiEye, FiCalendar } from 'react-icons/fi';
import SynthesisFirebaseService from './SynthesisFirebaseService';
import ConfirmationDialog from './ConfirmationDialog';

/**
 * Composant pour afficher la liste des synthèses sauvegardées
 */
const SynthesisList = ({ userId, competenceId, onSelectSynthesis }) => {
  const [syntheses, setSyntheses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSyntheses, setFilteredSyntheses] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    synthesisId: null,
    synthesisTitle: ''
  });
  const [errorDialog, setErrorDialog] = useState({
    isOpen: false,
    message: ''
  });

  useEffect(() => {
    loadSyntheses();
  }, [userId, competenceId]);

  useEffect(() => {
    filterSyntheses();
  }, [searchTerm, syntheses]);

  const loadSyntheses = async () => {
    if (!userId || !competenceId) return;
    
    try {
      setLoading(true);
      const result = await SynthesisFirebaseService.getUserSyntheses(userId, competenceId);
      if (result.success) {
        setSyntheses(result.syntheses || []);
      } else {
        console.error('Erreur:', result.error);
        setSyntheses([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des synthèses:', error);
      setSyntheses([]);
    } finally {
      setLoading(false);
    }
  };

  const filterSyntheses = () => {
    if (!searchTerm.trim()) {
      setFilteredSyntheses(syntheses);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = syntheses.filter(synthesis => {
      const objective = (synthesis.searchObjective || '').toLowerCase();
      const title = (synthesis.title || '').toLowerCase();
      return objective.includes(term) || title.includes(term);
    });

    setFilteredSyntheses(filtered);
  };

  const openDeleteDialog = (synthesisId, synthesisTitle, event) => {
    event.stopPropagation();
    setDeleteDialog({
      isOpen: true,
      synthesisId,
      synthesisTitle
    });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({
      isOpen: false,
      synthesisId: null,
      synthesisTitle: ''
    });
  };

  const confirmDelete = async () => {
    const { synthesisId } = deleteDialog;
    
    if (!userId || !competenceId || !synthesisId) return;
    
    try {
      const result = await SynthesisFirebaseService.deleteSynthesis(userId, competenceId, synthesisId);
      if (result.success) {
        setSyntheses(syntheses.filter(s => s.id !== synthesisId));
      } else {
        setErrorDialog({
          isOpen: true,
          message: 'Erreur lors de la suppression de la synthèse'
        });
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setErrorDialog({
        isOpen: true,
        message: 'Erreur lors de la suppression de la synthèse'
      });
    } finally {
      closeDeleteDialog();
    }
  };

  const closeErrorDialog = () => {
    setErrorDialog({
      isOpen: false,
      message: ''
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date inconnue';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date invalide';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Barre de recherche */}
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher une synthèse..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Liste des synthèses */}
      {filteredSyntheses.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {searchTerm ? 'Aucune synthèse trouvée' : 'Aucune synthèse sauvegardée'}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredSyntheses.map((synthesis) => (
            <div
              key={synthesis.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer w-full overflow-x-hidden"
              onClick={() => onSelectSynthesis(synthesis)}
            >
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 gap-3">
                <div className="flex-1 min-w-0 relative group">
                  <h3 className="text-lg font-semibold text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap sm:whitespace-normal sm:break-words">
                    {synthesis.searchObjective || synthesis.researchObjective || synthesis.title || 'Synthèse sans titre'}
                  </h3>
                  {/* Tooltip au hover avec fond bleu harmonisé - Amélioré pour mobile */}
                  <div className="invisible group-hover:visible absolute left-0 sm:left-1/2 sm:transform sm:-translate-x-1/2 top-full mt-2 p-3 bg-blue-600 text-white rounded-lg shadow-xl z-50 max-w-[90vw] sm:max-w-md text-sm sm:text-base leading-relaxed whitespace-normal break-words pointer-events-none">
                    {synthesis.searchObjective || synthesis.researchObjective || synthesis.title || 'Synthèse sans titre'}
                  </div>
                </div>
                <div className="flex gap-2 sm:ml-2 flex-shrink-0 justify-end sm:justify-start">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectSynthesis(synthesis);
                    }}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Voir la synthèse"
                  >
                    <FiEye size={18} />
                  </button>
                  <button
                    onClick={(e) => openDeleteDialog(
                      synthesis.id, 
                      synthesis.searchObjective || synthesis.researchObjective || synthesis.title || 'Synthèse sans titre',
                      e
                    )}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <FiTrash2 size={18} />
                  </button>
                </div>
              </div>

              <div className="flex items-center text-sm text-gray-500 mb-3">
                <FiCalendar className="mr-2" size={14} />
                {formatDate(synthesis.createdAt)}
              </div>

              {synthesis.summary && (
                <p className="text-gray-600 text-sm line-clamp-3 mb-3">
                  {synthesis.summary}
                </p>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                {(synthesis.sourcesUsed || synthesis.sources) && (synthesis.sourcesUsed?.length > 0 || synthesis.sources?.length > 0) && (
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">
                    {(synthesis.sourcesUsed?.length || synthesis.sources?.length || 0)} source{(synthesis.sourcesUsed?.length || synthesis.sources?.length || 0) > 1 ? 's' : ''}
                  </span>
                )}
                
                {synthesis.category && (
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    synthesis.category === 'inspiration' 
                      ? 'bg-yellow-100 text-yellow-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {synthesis.category === 'inspiration' ? '✨ Inspiration' : '📚 Général'}
                  </span>
                )}
                
                {synthesis.language && (
                  <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium">
                    {synthesis.language.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialogue de confirmation de suppression */}
      <ConfirmationDialog
        isOpen={deleteDialog.isOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        title="Supprimer la synthèse"
        message={`Êtes-vous sûr de vouloir supprimer la synthèse "${deleteDialog.synthesisTitle}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />

      {/* Dialogue d'erreur */}
      <ConfirmationDialog
        isOpen={errorDialog.isOpen}
        onClose={closeErrorDialog}
        onConfirm={closeErrorDialog}
        title="Erreur"
        message={errorDialog.message}
        confirmText="OK"
        cancelText={null}
        type="danger"
      />
    </div>
  );
};

export default SynthesisList;
